import { useRef, useState, type PointerEvent as EventoPonteiro } from 'react';
import type { CaixaMovel, ConfigPlanner, ConfigPosicaoLivre, MedidasPagina } from '@miolos/core';
import { ROTULO_CAIXA_MOVEL, encaixarCaixaLivre } from '@miolos/core';
import { comPosicao, posicoesDo } from '@/lib/posicoes-livres';

/** Distância (em mm) em que a caixa "cola" no alinhamento. */
const COLA_MM = 2;
/** Passo das setas do teclado, em mm. */
const PASSO_MM = 1;
const PASSO_GRANDE_MM = 5;
/** Tamanho da alça do canto, em mm. */
const ALCA_MM = 5;
const AZUL = '#2563eb';
const VERMELHO = '#e11d48';

interface Props {
  config: ConfigPlanner;
  /** A régua da folha, a mesma que desenhou a página de baixo. */
  medidas: MedidasPagina;
  /** Caixas que existem nesta folha: a área de escrita e os quadros ligados. */
  chaves: CaixaMovel[];
  aoMudar: (posicoes: NonNullable<ConfigPlanner['posicoesLivres']>) => void;
}

interface Arrasto {
  chave: CaixaMovel;
  /** 'mover' desloca a caixa; 'esticar' muda a largura e a altura. */
  modo: 'mover' | 'esticar';
  /** Ponteiro no começo do arrasto, em milímetros da folha. */
  inicioXMm: number;
  inicioYMm: number;
  /** A caixa como estava quando o arrasto começou. */
  original: ConfigPosicaoLivre;
  /** Guias acesas pelo alinhamento. */
  guiaX: number | null;
  guiaY: number | null;
}

/**
 * As alças de arrastar as caixas em cima da prévia.
 *
 * A camada é desenhada **dentro da folha**, esticada por cima do desenho: por
 * isso as alças se posicionam em milímetros, exatamente como a página impressa faz
 * — o que você arrasta é o que sai no papel, sem conta de escala no meio.
 *
 * O conteúdo anda quando o botão é solto, e não a cada pixel: montar a agenda
 * inteira de novo custa cerca de 70 ms por página, e refazer isso a cada
 * movimento deixaria o arrasto pesado. Enquanto o dedo está na tela, quem segue é
 * o contorno da caixa.
 *
 * As alças encolhem junto com a página — elas são do papel, não da tela. Numa
 * prévia pequena, o zoom da prévia (até 400%) devolve o conforto do ajuste fino.
 */
export function PosicionadorDeCaixas({ config, medidas, chaves, aoMudar }: Props) {
  const raiz = useRef<HTMLDivElement>(null);
  const arrastoRef = useRef<Arrasto | null>(null);
  const rascunhoRef = useRef<ConfigPosicaoLivre | null>(null);
  /** O que segue o ponteiro: cópia do ref, só para o desenho. */
  const [arrasto, setArrasto] = useState<Arrasto | null>(null);
  const [rascunho, setRascunho] = useState<ConfigPosicaoLivre | null>(null);

  const posicoes = posicoesDo(config);

  /** Quantos milímetros vale um pixel da tela, agora. */
  const fatorMm = () => {
    const largura = raiz.current?.getBoundingClientRect().width ?? 0;
    return largura > 0 ? medidas.larguraMm / largura : 1;
  };

  /** Encaixa a caixa na área de conteúdo: fora da folha não existe papel. */
  const comLimites = (caixa: ConfigPosicaoLivre): ConfigPosicaoLivre => encaixarCaixaLivre(caixa, medidas);

  /** Cola nas bordas e no centro da área de conteúdo. */
  const alinhada = (
    caixa: ConfigPosicaoLivre,
  ): { caixa: ConfigPosicaoLivre; guiaX: number | null; guiaY: number | null } => {
    const colunas = [0, (medidas.larguraConteudoMm - caixa.larguraMm) / 2, medidas.larguraConteudoMm - caixa.larguraMm];
    const linhas = [0, (medidas.alturaConteudoMm - caixa.alturaMm) / 2, medidas.alturaConteudoMm - caixa.alturaMm];

    let guiaX: number | null = null;
    let guiaY: number | null = null;
    let xMm = caixa.xMm;
    let yMm = caixa.yMm;

    for (const alvo of colunas) {
      if (guiaX === null && Math.abs(xMm - alvo) <= COLA_MM) {
        xMm = alvo;
        guiaX = alvo + caixa.larguraMm / 2;
      }
    }
    for (const alvo of linhas) {
      if (guiaY === null && Math.abs(yMm - alvo) <= COLA_MM) {
        yMm = alvo;
        guiaY = alvo + caixa.alturaMm / 2;
      }
    }

    return { caixa: { ...caixa, xMm, yMm }, guiaX, guiaY };
  };

  /** Milímetros do ponteiro, contados do canto da área de conteúdo. */
  const pontoEmMm = (evento: EventoPonteiro<HTMLElement>) => {
    const caixaDaCamada = raiz.current?.getBoundingClientRect();
    if (!caixaDaCamada) return { xMm: 0, yMm: 0 };
    const fator = fatorMm();
    return {
      xMm: (evento.clientX - caixaDaCamada.left) * fator - medidas.conteudoXMm,
      yMm: (evento.clientY - caixaDaCamada.top) * fator - medidas.conteudoYMm,
    };
  };

  const comecar = (evento: EventoPonteiro<HTMLElement>, chave: CaixaMovel, modo: Arrasto['modo']) => {
    const posicao = posicoes[chave];
    if (!posicao) return;

    evento.preventDefault();
    evento.stopPropagation();
    evento.currentTarget.setPointerCapture(evento.pointerId);

    const ponto = pontoEmMm(evento);
    const novo: Arrasto = {
      chave,
      modo,
      inicioXMm: ponto.xMm,
      inicioYMm: ponto.yMm,
      original: posicao,
      guiaX: null,
      guiaY: null,
    };

    /*
     * O arrasto vive num ref, e o estado só serve para desenhar. É o que garante
     * que ele termine **uma vez só**: `pointerup` e `lostpointercapture` chegam
     * quase juntos, e o segundo veria o estado ainda por atualizar.
     */
    arrastoRef.current = novo;
    rascunhoRef.current = posicao;
    setArrasto(novo);
    setRascunho(posicao);
  };

  const emMovimento = (evento: EventoPonteiro<HTMLElement>) => {
    const atual = arrastoRef.current;
    if (!atual) return;

    const ponto = pontoEmMm(evento);
    const andouX = ponto.xMm - atual.inicioXMm;
    const andouY = ponto.yMm - atual.inicioYMm;
    const original = atual.original;

    const pedida: ConfigPosicaoLivre =
      atual.modo === 'mover'
        ? { ...original, xMm: original.xMm + andouX, yMm: original.yMm + andouY }
        : { ...original, larguraMm: original.larguraMm + andouX, alturaMm: original.alturaMm + andouY };

    const { caixa, guiaX, guiaY } = alinhada(comLimites(pedida));
    rascunhoRef.current = caixa;
    setRascunho(caixa);
    setArrasto({ ...atual, guiaX, guiaY });
  };

  /** Fim do arrasto: guarda a posição que ficou no contorno. */
  const encerrar = (guardar: boolean) => {
    const atual = arrastoRef.current;
    if (!atual) return;

    arrastoRef.current = null;
    const posicao = rascunhoRef.current;
    rascunhoRef.current = null;
    setArrasto(null);
    setRascunho(null);

    if (guardar && posicao) aoMudar(comPosicao(config, atual.chave, posicao));
  };

  const soltar = (evento: EventoPonteiro<HTMLElement>) => {
    evento.currentTarget.releasePointerCapture?.(evento.pointerId);
    encerrar(true);
  };

  /**
   * A captura do ponteiro se perdeu (o dedo saiu da janela, o navegador cancelou
   * o gesto): o arrasto acaba aqui, sem deixar a caixa presa ao ponteiro.
   */
  const perdeuOCaptura = () => encerrar(true);

  /** Setas do teclado: ajuste fino de milímetro em milímetro (Shift = 5 mm). */
  const tecla = (evento: React.KeyboardEvent<HTMLElement>, chave: CaixaMovel) => {
    const posicao = posicoes[chave];
    if (!posicao) return;

    const passo = evento.shiftKey ? PASSO_GRANDE_MM : PASSO_MM;
    const andar: Record<string, [number, number]> = {
      ArrowLeft: [-passo, 0],
      ArrowRight: [passo, 0],
      ArrowUp: [0, -passo],
      ArrowDown: [0, passo],
    };
    const delta = andar[evento.key];
    if (evento.key === 'Escape') {
      // Esc cancela o arrasto em curso e devolve a caixa ao lugar de onde saiu.
      encerrar(false);
      return;
    }
    if (!delta) return;

    evento.preventDefault();
    const { caixa } = alinhada(comLimites({ ...posicao, xMm: posicao.xMm + delta[0], yMm: posicao.yMm + delta[1] }));
    aoMudar(comPosicao(config, chave, caixa));
  };

  return (
    <div
      ref={raiz}
      data-posicionador
      style={{ position: 'absolute', inset: 0, zIndex: 2000, pointerEvents: 'none' }}
    >
      {/* As guias de alinhamento, acesas só quando a caixa cola em alguma borda. */}
      {arrasto && arrasto.guiaX !== null ? (
        <div
          style={{
            position: 'absolute',
            left: `${medidas.conteudoXMm + arrasto.guiaX}mm`,
            top: `${medidas.conteudoYMm}mm`,
            width: 0,
            height: `${medidas.alturaConteudoMm}mm`,
            borderLeft: `0.2mm dashed ${VERMELHO}`,
          }}
        />
      ) : null}
      {arrasto && arrasto.guiaY !== null ? (
        <div
          style={{
            position: 'absolute',
            left: `${medidas.conteudoXMm}mm`,
            top: `${medidas.conteudoYMm + arrasto.guiaY}mm`,
            height: 0,
            width: `${medidas.larguraConteudoMm}mm`,
            borderTop: `0.2mm dashed ${VERMELHO}`,
          }}
        />
      ) : null}

      {/* A área de conteúdo: é dentro dela que a caixa pode ficar. */}
      <div
        style={{
          position: 'absolute',
          left: `${medidas.conteudoXMm}mm`,
          top: `${medidas.conteudoYMm}mm`,
          width: `${medidas.larguraConteudoMm}mm`,
          height: `${medidas.alturaConteudoMm}mm`,
          outline: `0.2mm dashed ${AZUL}59`,
        }}
      />

      {chaves.map((chave) => {
        const posicao = posicoes[chave];
        if (!posicao) return null;

        const desenhada = arrasto?.chave === chave && rascunho ? rascunho : posicao;
        const arrastando = arrasto?.chave === chave;

        return (
          <div
            key={chave}
            data-alca={chave}
            role="button"
            tabIndex={0}
            aria-label={`${ROTULO_CAIXA_MOVEL[chave]}: arraste para mover, setas do teclado para ajustar de milímetro em milímetro`}
            onPointerDown={(evento) => comecar(evento, chave, 'mover')}
            onPointerMove={emMovimento}
            onPointerUp={soltar}
            onPointerCancel={soltar}
            onLostPointerCapture={perdeuOCaptura}
            onKeyDown={(evento) => tecla(evento, chave)}
            style={{
              position: 'absolute',
              left: `${medidas.conteudoXMm + desenhada.xMm}mm`,
              top: `${medidas.conteudoYMm + desenhada.yMm}mm`,
              width: `${desenhada.larguraMm}mm`,
              height: `${desenhada.alturaMm}mm`,
              boxSizing: 'border-box',
              border: arrastando ? `0.5mm solid ${AZUL}` : `0.3mm dashed ${AZUL}`,
              background: arrastando ? 'rgba(37, 99, 235, 0.12)' : 'rgba(37, 99, 235, 0.04)',
              cursor: arrastando ? 'grabbing' : 'grab',
              pointerEvents: 'auto',
              touchAction: 'none',
            }}
          >
            <span
              style={{
                position: 'absolute',
                top: 0,
                left: '-0.3mm',
                transform: 'translateY(-100%)',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: '9pt',
                lineHeight: 1.5,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: AZUL,
                whiteSpace: 'nowrap',
                padding: '0 1mm',
                background: 'rgba(255, 255, 255, 0.88)',
              }}
            >
              {ROTULO_CAIXA_MOVEL[chave]}
              {arrastando
                ? ` · ${Math.round(desenhada.xMm)}, ${Math.round(desenhada.yMm)} mm`
                : ` · ${Math.round(desenhada.larguraMm)} × ${Math.round(desenhada.alturaMm)} mm`}
            </span>

            {/* Alça do canto: estica a caixa. */}
            <span
              data-alca-canto={chave}
              onPointerDown={(evento) => comecar(evento, chave, 'esticar')}
              onPointerMove={emMovimento}
              onPointerUp={soltar}
              onPointerCancel={soltar}
              onLostPointerCapture={perdeuOCaptura}
              style={{
                position: 'absolute',
                right: `-${ALCA_MM / 2}mm`,
                bottom: `-${ALCA_MM / 2}mm`,
                width: `${ALCA_MM}mm`,
                height: `${ALCA_MM}mm`,
                border: `0.3mm solid ${AZUL}`,
                background: '#fff',
                cursor: 'nwse-resize',
                pointerEvents: 'auto',
                touchAction: 'none',
              }}
            />
          </div>
        );
      })}
    </div>
  );
}
